import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { catchError, filter, map, Observable, switchMap, tap, throwError } from 'rxjs';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { SessionStorageService } from 'ngx-webstorage';
import { Router } from '@angular/router';
import firebase from 'firebase/compat/app';
import * as _ from 'lodash';
import User = firebase.User;
import { HelpersService } from './helpers.service';
import { ErrorMessages } from '@app/shared/constants';
import { AdminScopes } from './admin-scopes';

export { AdminScopes } from './admin-scopes';

export interface AdminUser {
  uid: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  scopes: AdminScopes[];
}
@Injectable({ providedIn: 'root' })
export class UserService {
  constructor(
    private firebaseAuth: AngularFireAuth,
    private helperService: HelpersService,
    private router: Router,
    private sessionStorageService: SessionStorageService,
    private http: HttpClient
  ) {}

  public getCurrentUser(): AdminUser {
    return this.sessionStorageService.retrieve('currentUser');
  }

  public storeCurrentUser(currentUser: AdminUser): void {
    this.sessionStorageService.store('currentUser', currentUser);
  }

  public getAuthenticatedUser(scopes?: AdminScopes[]): Observable<any> {
    let fbUser: User;
    return this.firebaseAuth.user.pipe(
      // Get the currently authenticated user from Firebase
      filter((user: any) => user !== null),
      tap((user: User) => (fbUser = user)),
      // Get the idToken for that user
      switchMap((user: User) =>
        user ? user.getIdToken(true) : throwError(() => new Error(ErrorMessages.UNAUTHORIZED))
      ),
      // Get the currently authenticated user from app-services
      switchMap((idToken: string) => {
        this.sessionStorageService.store('idToken', idToken);
        return this.getCurrentAccount(fbUser.uid, idToken, scopes);
      }),
      catchError((error: any) => throwError((err) => new Error(error)))
    );
  }

  public getAdminUsers(): Observable<any> {
    return this.http.get<AdminUser[]>(`${environment.APP_SERVICES_URL}/admin/admin_users`).pipe(
      map((res) => {
        return res;
      }),
      catchError((error: any) => throwError(() => ErrorMessages.UNEXPECTED))
    );
  }

  public updateAdminUsers(id, scopes): Observable<any> {
    return this.http.put<AdminUser>(`${environment.APP_SERVICES_URL}/admin/admin_users/${id}`, { scopes }).pipe(
      map((res) => {
        return res;
      }),
      catchError((error: any) => throwError(() => ErrorMessages.UNEXPECTED))
    );
  }

  public hasAuthScope(adminUser: AdminUser, scopes?: AdminScopes[]): boolean {
    return scopes.every((scope: AdminScopes) => adminUser.scopes.includes(scope));
  }

  /**
   * Check if user has access based on required scopes and prevented scopes
   *
   * This method provides a flexible authorization system that can:
   * 1. Require specific scopes (user must have ALL of them)
   * 2. Prevent access if user has specific scopes (user must have NONE of them)
   * 3. Combine both requirements
   *
   * Examples:
   * - hasAccess(user, [VIEW_SHIPMENTS]) - User must have VIEW_SHIPMENTS
   * - hasAccess(user, undefined, [LIMIT_RIVO]) - User must NOT have LIMIT_RIVO
   * - hasAccess(user, [VIEW_SHIPMENTS], [LIMIT_RIVO]) - User must have VIEW_SHIPMENTS AND NOT have LIMIT_RIVO
   *
   * @param adminUser - The admin user to check
   * @param requiredScopes - Scopes the user must have (all of them)
   * @param preventedScopes - Scopes that will deny access if user has any of them
   * @returns boolean - true if user has access, false otherwise
   */
  public hasAccess(adminUser: AdminUser, requiredScopes?: AdminScopes[], preventedScopes?: AdminScopes[]): boolean {
    // Check if user has all required scopes
    if (requiredScopes && requiredScopes.length > 0) {
      const hasRequiredScopes = requiredScopes.every((scope: AdminScopes) => adminUser.scopes.includes(scope));
      if (!hasRequiredScopes) return false;
    }

    // Check if user has any prevented scopes
    if (preventedScopes && preventedScopes.length > 0) {
      const hasPreventedScope = preventedScopes.some((scope: AdminScopes) => adminUser.scopes.includes(scope));
      if (hasPreventedScope) return false;
    }

    return true;
  }

  /**
   * Convenience method for checking access in components
   * This method uses the current authenticated user
   *
   * @param requiredScopes - Scopes the user must have (all of them)
   * @param preventedScopes - Scopes that will deny access if user has any of them
   * @returns boolean - true if user has access, false otherwise
   */
  public canAccess(requiredScopes?: AdminScopes[], preventedScopes?: AdminScopes[]): boolean {
    const currentUser = this.getCurrentUser();
    if (!currentUser) return false;
    return this.hasAccess(currentUser, requiredScopes, preventedScopes);
  }

  public getFromAll(query: {
    email: string;
    firstName?: string;
    lastName?: string;
    companyName?: string;
    role?: 'U' | 'A' | 'O' | 'M';
    fraudulent?: string;
  }): Observable<any> {
    return this.http
      .get(`${environment.APP_SERVICES_URL}/admin/users/search`, {
        params: { ...query, excludeClosed: 'true' },
      })
      .pipe(
        map((res: any) => {
          // Exclude closed accounts in case backend does not filter by excludeClosed
          return Array.isArray(res) ? res.filter((user: any) => !user?.company?.is_closed) : res;
        }),
        catchError((error: any) => throwError(() => ErrorMessages.UNEXPECTED))
      );
  }

  private getCurrentAccount(fbUid: any, fbIdToken: string, scopes?: AdminScopes[]): Observable<AdminUser> {
    const params = scopes && scopes.length > 0 ? { scope: scopes } : {};
    return this.http
      .get<AdminUser>(`${environment.APP_SERVICES_URL}/admin/current`, {
        headers: {
          uid: fbUid,
          authorization: 'Bearer ' + fbIdToken,
        },
        params,
      })
      .pipe(
        map((adminUser: AdminUser) => {
          this.storeCurrentUser(_.extend(adminUser, { idToken: fbIdToken }));
          return adminUser;
        }),
        catchError((error: any) => throwError(() => ErrorMessages.UNEXPECTED))
      );
  }

  public verifyUser(params: { email: string }): Observable<any> {
    return this.http.post(`${environment.APP_SERVICES_URL}/admin/users/verify_account`, params).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => throwError(() => ErrorMessages.UNEXPECTED))
    );
  }

  /**
   * Clears the user's IdP object id so their next SSO login binds afresh. Used when IT deletes and
   * recreates the person's account in the identity provider.
   */
  public resetSsoBinding(uuid: string): Observable<void> {
    return this.http
      .put<void>(`${environment.APP_SERVICES_URL}/admin/users/${uuid}/sso-binding/reset`, {})
      .pipe(catchError((error: any) => throwError(() => error.error || ErrorMessages.UNEXPECTED)));
  }

  public makeOwner(
    newOwner: { id: string; firstName: string; lastName: string; email: string},
    company: { id: string; uuid?: string },
  ): Observable<any> {
    if (!company.uuid) {
      return throwError(() => new Error('Company UUID is required'));
    }

    return this.http
      .put(`${environment.APP_SERVICES_URL}/admin/companies/${company.uuid}/change_owner_to/${newOwner.id}`, {newOwner: newOwner})
      .pipe(
        map((res: any) => res),
        catchError((error: any) => throwError(() => error))
      );
  }

  public closeAccount( companyUuid: string): Observable<any> {
    return this.http
      .post(`${environment.APP_SERVICES_URL}/admin/company/${companyUuid}/close_account`, {companyUuid})
      .pipe(
        map((res: any) => res),
        catchError((error: any) => {
          const fallback = 'Close account failed. Please try again or contact support.';
          try {
            console.error('Close account failed', error);
            const message =
              typeof error?.error === 'string'
                ? error.error
                : error?.error?.message;
            if (message && message !== ErrorMessages.UNAUTHORIZED) {
              alert(message);
            } else {
              alert(fallback);
            }
          } catch (e) {
            console.error(e);
          }
          return new Observable((subscriber) => subscriber.error(new Error(fallback)));
        })
      );
  }
}
